import { useState } from "react";
import { useError } from "../../contexts/ErrorContext";
import { 
    getPOIGroupsByJobAPI, 
    addPOIsToGroupAPI, 
    removePOIsFromGroupAPI,
    createPOIGroupAPI,
    updatePOIGroupAPI
} from "../../services/poiGroupsAPI";
import { getUsersPOIGroupsAPI } from "../../services/poiGroupUsersAPI";

/**
 * Makes API calls for POI Group related functionality
 * 
 * @returns {PoiGroupOption[]} poiGroupOptions - poi groups to fill dropdown on PoiGroupDialog
 * @returns {Object} usersGroupData - info about the poi groups that the user is in
 * @returns {Object} permissionSortedPoiGroups - nested options for react select
 * @returns {Function} setPermissionSortedPoiGroups - so components can update options when groups added, deleted, updated
 * @returns {Function} setPoiGroupOptions - setter function to set poi group options after fetch or save
 * @returns {Function} fetchPOIGroupsByJob - fetches all poi groups for a job
 * @returns {Function} fetchPOIGroupsUser - fetches poi groups for specific user
 * @returns {Function} formatPoiGroupOptions - formats poi groups from database into dropdown options
 * @returns {Function} updatePoiGroups - calls api to add or remove groups for a poi
 * @returns {Function} addNewPoiGroup - calls api to add new poi group, passes in array of pois to add to new group
 * 
 * @category Hooks - POI Map
 */
const usePoiGroupService = () => {
    const { showError } = useError();

    const [poiGroupOptions, setPoiGroupOptions] = useState([]); //group dropdown options
    const [usersGroupData, setUsersGroupData] = useState([]); //keep all the group data in case we need to know more about the group, like CreatedBy
    const [permissionSortedPoiGroups, setPermissionSortedPoiGroups] = useState([])

    const fetchPOIGroupsByJob = async (jobId, isMulti = false) => {

        const response = await getPOIGroupsByJobAPI(jobId)

        if (!response.success) {
            showError(`Error getting POI groups: ${response.message}`);
        } else {

            //format the group options
            const basicGroupOptions = formatPoiGroupOptions(response.data, isMulti)
            const nestedGroupOptions = formatGroupsForNestedSelect(response.data, isMulti)
            
            //set state
            setPoiGroupOptions(basicGroupOptions)
            setPermissionSortedPoiGroups(nestedGroupOptions)
        }
    };

    
    const fetchPOIGroupsByUser = async (jobId, isMulti = false) => {

        const response = await getUsersPOIGroupsAPI(jobId)

        if (!response.success) {
            showError(`Error getting POI groups: ${response.message}`);
        } else {
                
            const groupOptions = formatPoiGroupOptions(response.data, isMulti)

            setPoiGroupOptions(groupOptions)

            setUsersGroupData(response.data)

        }
        
    };

    const formatPoiGroupOptions = (poiGroups, isMulti = false) => {
        let selectOptions = []

        if(poiGroups.length > 0){
            selectOptions = poiGroups.map(poiGroup => {
                return {
                    label: poiGroup.POIGroup_Name,
                    value: poiGroup.POIGroupID,
                    ...(poiGroup.IsEditor !== undefined && { IsEditor: poiGroup.IsEditor })
                }
            })

            
        }
        if(!isMulti){
            const allPoisLabel = window.location.pathname === "/poi-edit" ? "All POIs for job" : "All POIs in my groups"
            const noPoisLabel =  "Don't show POIs"

            selectOptions.unshift({label: noPoisLabel, value: -1})
            selectOptions.unshift({label: allPoisLabel, value: 0})
        }

        return selectOptions
    }

    const formatGroupsForNestedSelect = (groups, isMulti) => {
        const editorGroups = [];
        const viewerGroups = [];
        const notInGroup = [];
    
        groups.forEach(group => {
            const option = {
                label: group.POIGroup_Name,
                value: group.POIGroupID,
                IsEditor: group.IsEditor,
                IsUserInGroup: group.isUserInGroup
            };

            if (group.isUserInGroup) {

                if (group.IsEditor) {
                    editorGroups.push(option);
                } else {
                    viewerGroups.push(option);
                }
            } else {
                notInGroup.push(option);
            }
        });
        
        // nest groups for react-select dropdown
        let nestedOptions = [
            { label: "Editor", options: editorGroups },
            { label: "Viewer", options: viewerGroups },
            { label: "Not In Group", options: notInGroup }
        ]
        
        if(!isMulti){
            const allPoisLabel = window.location.pathname === "/poi-edit" ? "All POIs for job" : "All POIs in my groups"
            const noPoisLabel =  "Don't show POIs"
            
            nestedOptions.unshift({label: noPoisLabel, value: -1})
            nestedOptions.unshift({label: allPoisLabel, value: 0}) 
            
        }
        
        return nestedOptions
    };

    const updatePoiGroups = async(selectedGroups, originalGroups, linkedPOIs, poiId, JobID) => {

        // get ids from current and original groups
        const groupIds = selectedGroups.map(group => group.value);
        const groupIdsOriginal = originalGroups.map(group => group.value); //remove "all groups" option

        // compare ids between current and original poi groups to determine which to add and remove
        const groupsToAdd = groupIds.filter(group => !groupIdsOriginal.includes(group)).map(group => group);
        const groupsToRemove = groupIdsOriginal.filter(group => !groupIds.includes(group)).map(group => group);

        //user may have updated names of linkedPOIs, so check for groupIds that are not to be added or removed
        let linkedPOIsToUpdate = []

        if(linkedPOIs && linkedPOIs.length > 0){
            linkedPOIsToUpdate = groupIds.filter(groupId => 
                !groupsToAdd.includes(groupId) &&
                !groupsToRemove.includes(groupId)
            ).map(groupId => {
                const linkedPOI = linkedPOIs.find(lpoi => lpoi.POIGroupID === groupId);
    
                return {
                    LPOIID: linkedPOI.LPOIID,
                    POI_Name: linkedPOI.POI_Name,
                    ...(linkedPOI.Description && { Description: linkedPOI.Description }),
                    POIGroupID: groupId,
                }
            }).filter(Boolean);
        }
        
        //if no changes, send message to user
        if(groupsToAdd.length === 0 && groupsToRemove.length === 0 && linkedPOIsToUpdate.length === 0){
            return {
                success: true,
                msg: "No changes made to POI Groups"
            }
        }

        //send linkedPOIs to api, if there are any
        if(linkedPOIsToUpdate.length > 0){
            for(const linkedPOI of linkedPOIsToUpdate){

                const response = await updatePOIGroupAPI(linkedPOI.POIGroupID, {linkedPOIs: [linkedPOI]})
                    
                if(!response.success){
                    return response
                }
            }
        }

        //add poi to groups - api call
        if(groupsToAdd.length > 0){
            for(const groupId of groupsToAdd){

                const linkedPOI = linkedPOIs.find(lpoi => lpoi.POIGroupID === groupId)

                const response = await addPOIsToGroupAPI(
                    groupId, 
                    [
                        {
                            POIID: poiId,
                            POI_Name: linkedPOI?.POI_Name ?? null,
                            Description: linkedPOI?.Description ?? null
                        }
                    ]
                )
                    
                if(!response.success){
                    return response
                }
            }
            
        }

        //remove poi from groups - api call
        if(groupsToRemove.length > 0){
            for(const groupId of groupsToRemove){
                const response = await removePOIsFromGroupAPI(groupId, [{POIID: poiId}])
                    
                if(!response.success){
                    return response
                }
            }
            
        }

        
        //get (user's) group data, map against currently selected groups, and return it so poi state can have updated groups
        const userGroupResponse = await getUsersPOIGroupsAPI(JobID)
        
        const usersGroupsForPOI = userGroupResponse.data.filter(userGroup => 
            selectedGroups.some(group => group.value === userGroup.POIGroupID)
        );

        return {
            success: true,
            data: {
                updatedGroups: usersGroupsForPOI
            }
        }

    }

    const addPoiToGroup = async(groupId, poi, JobID) => {
        const response = await addPOIsToGroupAPI(
            groupId, 
            [
                {
                    POIID: poi.POIID,
                    POI_Name: poi.POI_Name ?? null,
                    Description: poi.Description ?? null
                }
            ]
        )

        if(!response.success){
            return response
        }

        const usersGroupsForPOI = await getUserPoiGroupsAfterUpdate(JobID, poi.POIID)

        return {
            success: true,
            data: {
                updatedGroups: usersGroupsForPOI
            }
        }
    }

    const removePoiFromGroup = async(groupId, poi, JobID) => {
        const response = await removePOIsFromGroupAPI(groupId, [{POIID: poi.POIID}])
                    
        if(!response.success){
            return response
        }


        const usersGroupsForPOI = await getUserPoiGroupsAfterUpdate(JobID, poi.POIID)

        return {
            success: true,
            data: {
                updatedGroups: usersGroupsForPOI
            }
        }
    }

    const getUserPoiGroupsAfterUpdate = async(JobID, POIID) => {
        const userGroupResponse = await getUsersPOIGroupsAPI(JobID)
        
        const usersGroupsForPOI = userGroupResponse.data.filter(userGroup => userGroup.LinkedPOIs.some(lpoi => POIID === lpoi.POIID));

        return usersGroupsForPOI
    }

    const addNewPoiGroup = async(poiGroupData) => {

        const response = await createPOIGroupAPI(poiGroupData)

        //if successful, add to group dropdown options
        if(response.success){
            const newGroup = response.data

            setPoiGroupOptions(prevOptions => {
                const updatedOptions = [...prevOptions]

                
                const newOption = {
                    label: newGroup.POIGroup_Name,
                    value: newGroup.POIGroupID,
                    IsEditor: true
                }

                return [...updatedOptions, newOption]
            })

            setPermissionSortedPoiGroups(prevOptions => {
                const newEditorOption = {
                    label: newGroup.POIGroup_Name,
                    value: newGroup.POIGroupID,
                    IsEditor: true,
                    IsUserInGroup: true
                }
                

                const newOptions = [...prevOptions];

                const editorGroupIndex = newOptions.findIndex(
                    (group) => group.label === "Editor"
                );

                if (editorGroupIndex !== -1) {
                    newOptions[editorGroupIndex] = {
                        ...newOptions[editorGroupIndex],
                        options: [
                            ...newOptions[editorGroupIndex].options,
                            newEditorOption
                        ]
                    };
                }

                return newOptions;



            })

            
        }

        return response

    }

    const updateLinkedPoi  = async(poiGroupId, linkedPOI) => {
        const response = await updatePOIGroupAPI(poiGroupId, {linkedPOIs: [linkedPOI]})
                    
        if(!response.success){
            return response
        }

        return {
            success: true
        }
    }

    return {
        poiGroupOptions,
        usersGroupData,
        permissionSortedPoiGroups,
        setPermissionSortedPoiGroups,
        setPoiGroupOptions, 
        fetchPOIGroupsByJob,
        fetchPOIGroupsByUser,
        formatPoiGroupOptions,
        updatePoiGroups,
        addNewPoiGroup,
        addPoiToGroup,
        removePoiFromGroup,
        updateLinkedPoi
    };
};

export default usePoiGroupService;
